import React, { useEffect, useState } from 'react';
import { adminAPI } from '../../services/api';
import { Cake, Star, Gift } from 'lucide-react';

const MONTHS = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

function EventCard({ icon: Icon, iconColor, iconBg, name, department, detail, highlight }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '9px 10px', borderRadius: 8,
      background: highlight ? iconBg : 'var(--gray-50)',
      border: `1px solid ${highlight ? iconColor + '30' : 'var(--gray-100)'}`,
    }}>
      <div style={{
        width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
        background: highlight ? iconColor : 'var(--gray-200)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon size={16} color={highlight ? 'white' : 'var(--gray-500)'} strokeWidth={1.8} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--gray-900)' }} className="truncate">
          {name}
        </div>
        <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{department || '—'}</div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: highlight ? iconColor : 'var(--gray-600)' }}>
          {detail}
        </div>
      </div>
    </div>
  );
}

function Section({ title, subtitle, items, icon: Icon, iconColor, iconBg, emptyText, renderDetail, highlight }) {
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon size={17} color={iconColor} strokeWidth={1.8} />
          <div>
            <div className="card-title">{title}</div>
            {subtitle && <div className="card-subtitle">{subtitle}</div>}
          </div>
        </div>
        <span style={{
          fontSize: 12, fontWeight: 600, padding: '2px 8px', borderRadius: 12,
          background: iconBg, color: iconColor,
        }}>{items.length}</span>
      </div>
      {items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '16px 0', fontSize: 13, color: 'var(--gray-400)' }}>
          {emptyText}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {items.map((item, i) => (
            <EventCard
              key={i}
              icon={highlight ? Icon : Icon}
              iconColor={iconColor}
              iconBg={iconBg}
              name={`${item.first_name} ${item.last_name}`}
              department={item.department}
              detail={renderDetail(item)}
              highlight={highlight}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function UpcomingEvents() {
  const today = new Date();
  const thisMonth = today.getMonth() + 1;
  const nextMonth = thisMonth === 12 ? 1 : thisMonth + 1;
  const thisMonthName = MONTHS[today.getMonth()];
  const nextMonthName = MONTHS[nextMonth - 1];

  const [bThis,  setBThis]  = useState([]);
  const [bNext,  setBNext]  = useState([]);
  const [aThis,  setAThis]  = useState([]);
  const [aNext,  setANext]  = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      adminAPI.getBirthdays({ month: thisMonth }),
      adminAPI.getBirthdays({ month: nextMonth }),
      adminAPI.getAnniversaries({ month: thisMonth }),
      adminAPI.getAnniversaries({ month: nextMonth }),
    ]).then(([b1, b2, a1, a2]) => {
      setBThis(b1.data); setBNext(b2.data);
      setAThis(a1.data); setANext(a2.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return null;

  const daysLabel  = (n) => n <= 5 ? 'var(--primary)' : 'var(--gray-600)';
  const totalEvents = bThis.length + bNext.length + aThis.length + aNext.length;
  if (totalEvents === 0) return null;

  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--gray-900)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
        <Gift size={17} color="var(--primary)" strokeWidth={1.8} />
        Próximos eventos
      </div>

      <div className="events-grid">
        <Section
          title={`Cumpleaños — ${thisMonthName}`}
          items={bThis}
          icon={Cake}
          iconColor="#d97706"
          iconBg="#fffbeb"
          emptyText="Sin cumpleaños este mes"
          highlight={true}
          renderDetail={item => `Día ${parseInt(item.birth_day)}`}
        />
        <Section
          title={`Cumpleaños — ${nextMonthName}`}
          items={bNext}
          icon={Cake}
          iconColor="#9ca3af"
          iconBg="#f3f4f6"
          emptyText="Sin cumpleaños el mes siguiente"
          highlight={false}
          renderDetail={item => `Día ${parseInt(item.birth_day)}`}
        />
        <Section
          title={`Aniversarios — ${thisMonthName}`}
          subtitle="Generan días de vacaciones"
          items={aThis}
          icon={Star}
          iconColor="#2563eb"
          iconBg="#eff6ff"
          emptyText="Sin aniversarios este mes"
          highlight={true}
          renderDetail={item => `${item.years_completing} año${item.years_completing > 1 ? 's' : ''} · +${item.days_granted}d`}
        />
        <Section
          title={`Aniversarios — ${nextMonthName}`}
          items={aNext}
          icon={Star}
          iconColor="#9ca3af"
          iconBg="#f3f4f6"
          emptyText="Sin aniversarios el mes siguiente"
          highlight={false}
          renderDetail={item => `${item.years_completing} año${item.years_completing > 1 ? 's' : ''} · +${item.days_granted}d`}
        />
      </div>
    </div>
  );
}